import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Spin, Empty, message as antMessage, Modal, Switch, Space } from 'antd';
import { GlobalOutlined, LockOutlined } from '@ant-design/icons';
import RGL from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import dashboardService from '@/services/dashboardService';
import { Dashboard, DashboardWidget as DashboardWidgetType } from '@/types/dashboard';
import { DashboardWidget } from '@/components/DashboardWidget';

const { Title, Text } = Typography;

// Custom styles for grid placeholder
const gridStyles = `
  .react-grid-placeholder {
    background: rgba(24, 144, 255, 0.15) !important;
    border: 2px solid #1890ff !important;
    border-radius: 8px !important;
    opacity: 1 !important;
    transition-duration: 100ms;
    z-index: 2;
  }
`;

const DashboardPage = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const navigate = useNavigate();
  
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [widgets, setWidgets] = useState<DashboardWidgetType[]>([]);
  const [loading, setLoading] = useState(true);
  const saveTimeoutRef = useRef<number | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Load dashboard data
  useEffect(() => {
    if (dashboardId) {
      loadDashboard(dashboardId);
    } else {
      // Load first dashboard or show empty state
      loadFirstDashboard();
    }
  }, [dashboardId]);

  // Listen for custom events from sidebar
  useEffect(() => {
    const handleLoadDashboard = (event: CustomEvent) => {
      const { dashboardId: newDashboardId } = event.detail;
      loadDashboard(newDashboardId);
    };

    const handleNewDashboard = () => {
      setDashboard(null);
      setWidgets([]);
      setLoading(false);
    };

    window.addEventListener('load-dashboard', handleLoadDashboard as EventListener);
    window.addEventListener('new-dashboard', handleNewDashboard);

    return () => {
      window.removeEventListener('load-dashboard', handleLoadDashboard as EventListener);
      window.removeEventListener('new-dashboard', handleNewDashboard);
    };
  }, []);

  const loadFirstDashboard = async () => {
    try {
      const response = await dashboardService.getDashboards();
      if (response.success && response.dashboards.length > 0) {
        const firstDashboard = response.dashboards[0];
        navigate(`/dashboard/${firstDashboard.id}`, { replace: true });
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to load dashboards:', error);
      setLoading(false);
    }
  };

  const loadDashboard = async (id: string) => {
    setLoading(true);
    try {
      const response = await dashboardService.getDashboard(id);
      if (response.success) {
        setDashboard(response.dashboard);
        setWidgets(response.widgets);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      antMessage.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const saveLayout = useCallback((newLayout: any[]) => {
    if (!dashboard || newLayout.length === 0) {
      return;
    }

    // Map grid layout to our layout format
    const layouts = newLayout.map(item => ({
      widgetId: item.i,
      layout: {
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h
      }
    }));

    // Update local state
    setWidgets(prevWidgets =>
      prevWidgets.map(widget => {
        const newLayoutItem = layouts.find(l => l.widgetId === widget.id);
        if (!newLayoutItem) return widget;
        
        // Only update if layout actually changed
        if (
          widget.layout.x === newLayoutItem.layout.x &&
          widget.layout.y === newLayoutItem.layout.y &&
          widget.layout.w === newLayoutItem.layout.w &&
          widget.layout.h === newLayoutItem.layout.h
        ) {
          return widget;
        }
        
        return { ...widget, layout: newLayoutItem.layout };
      })
    );

    // Clear any pending save timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce the API save
    const dashboardId = dashboard.id;
    saveTimeoutRef.current = setTimeout(() => {
      dashboardService.updateLayout(dashboardId, { layouts })
        .then(() => console.log('[DASHBOARD] Layout saved'))
        .catch(error => {
          console.error('[DASHBOARD] Failed to save layout:', error);
          antMessage.error('Failed to save layout');
        });
    }, 500);
  }, [dashboard]);

  const handleDeleteWidget = async (widgetId: string) => {
    if (!dashboard) return;

    Modal.confirm({
      title: 'Remove Widget',
      content: 'Are you sure you want to remove this widget from the dashboard?',
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await dashboardService.removeWidget(dashboard.id, widgetId);
          setWidgets(prevWidgets => prevWidgets.filter(w => w.id !== widgetId));
          antMessage.success('Widget removed');
        } catch (error) {
          console.error('Failed to remove widget:', error);
          antMessage.error('Failed to remove widget');
        }
      }
    });
  };

  const handleToggleVisibility = async (isPublic: boolean) => {
    if (!dashboard) return;

    try {
      await dashboardService.toggleVisibility(dashboard.id, isPublic);
      setDashboard({ ...dashboard, is_public: isPublic });
      antMessage.success(isPublic ? 'Dashboard is now public' : 'Dashboard is now private');
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
      antMessage.error('Failed to update visibility');
    }
  };

  // Drag/resize handlers - only save when user stops interacting
  const handleDragStop = useCallback((layout: any) => {
    saveLayout(layout);
  }, [saveLayout]);

  const handleResizeStop = useCallback((layout: any) => {
    saveLayout(layout);
  }, [saveLayout]);

  // Convert widgets to grid layout format - memoized to prevent recreating on every render
  // MUST be before any conditional returns to satisfy Rules of Hooks
  const layout = useMemo(() => 
    widgets.map(widget => ({
      i: widget.id,
      x: widget.layout.x,
      y: widget.layout.y,
      w: widget.layout.w,
      h: widget.layout.h,
      minW: 2,
      minH: 2
    })),
    [widgets]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="flex items-center justify-center h-96">
        <Empty
          description={
            <div>
              <Text className="text-gray-600 block mb-4">
                No dashboard found. Create one to get started.
              </Text>
              <Text type="secondary" className="text-sm">
                Tip: Add charts from Explore to your dashboards!
              </Text>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div>
      {/* Custom Grid Styles */}
      <style>{gridStyles}</style>
      
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <Title level={2} className="mb-2 font-semibold">
            {dashboard.title}
          </Title>
          <Text type="secondary">
            {widgets.length} widget{widgets.length !== 1 ? 's' : ''}
          </Text>
        </div>
        
        {/* Owner controls or creator info */}
        {dashboard.is_owner ? (
          <Space align="center" className="mt-2">
            <Switch
              checked={dashboard.is_public}
              onChange={handleToggleVisibility}
              checkedChildren={
                <Space size={4}>
                  <GlobalOutlined />
                  <span>Public</span>
                </Space>
              }
              unCheckedChildren={
                <Space size={4}>
                  <LockOutlined />
                  <span>Private</span>
                </Space>
              }
            />
          </Space>
        ) : (
          <div className="mt-2">
            <Text type="secondary" className="text-sm">
              Created by {dashboard.creator?.display_name || dashboard.creator?.email || 'Unknown'}
            </Text>
          </div>
        )}
      </div>

      {/* Empty state for no widgets */}
      {widgets.length === 0 ? (
        <div className="flex items-center justify-center h-96">
          <Empty
            description={
              <div>
                <Text className="text-gray-600 block mb-4">
                  No widgets yet. Add charts from Explore to get started.
                </Text>
                <Text type="secondary" className="text-sm">
                  Navigate to Explore, generate a chart, and click "Add to Dashboard"
                </Text>
              </div>
            }
          />
        </div>
      ) : (
        <RGL
          className="layout"
          layout={layout}
          // @ts-expect-error - Type definitions for react-grid-layout conflict with Ant Design Layout
          cols={12}
          rowHeight={80}
          width={1200}
          onDragStop={handleDragStop}
          onResizeStop={handleResizeStop}
          draggableHandle=".ant-card-head"
          isDraggable={true}
          isResizable={true}
          compactType={null}
        >
          {widgets.map(widget => (
            <div key={widget.id}>
              <DashboardWidget
                widget={widget}
                onDelete={handleDeleteWidget}
              />
            </div>
          ))}
        </RGL>
      )}
    </div>
  );
};

export default DashboardPage;
